/**
 * Admin Service
 *
 * API service for admin dashboard functionality (Director only).
 */

import type {
    AdminClassGroup,
    AdminClassLessonPlanItem,
    AdminContract,
    BulkClassPriceRequest,
    AdminLesson,
    AdminLevel,
    AdminStudent,
    AdminTeacher,
    ClassLessonPlanResponse,
    CreateClassGroupRequest,
    CreateContractOverrideRequest,
    CreateLessonRequest,
    CreateLevelRequest,
    CreateStudentRequest,
    CreateTeacherRequest,
    MoveStudentRequest,
    UpdateClassGroupRequest,
    UpdateContractRequest,
    UpdateLessonRequest,
    UpdateLevelRequest,
    UpdateStudentRequest,
    UpdateTeacherRequest,
} from "../types/admin";
import api from "./api";

// Helper to convert snake_case to camelCase
function toCamelCase(obj: Record<string, unknown>): Record<string, unknown> {
  if (obj === null || typeof obj !== "object") return obj;
  if (Array.isArray(obj))
    return obj.map((item) =>
      toCamelCase(item as Record<string, unknown>),
    ) as unknown as Record<string, unknown>;

  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) =>
      letter.toUpperCase(),
    );
    result[camelKey] =
      typeof value === "object" && value !== null
        ? toCamelCase(value as Record<string, unknown>)
        : value;
  }
  return result;
}

// Helper to convert camelCase to snake_case
function toSnakeCase(obj: Record<string, unknown>): Record<string, unknown> {
  if (obj === null || typeof obj !== "object") return obj;
  if (Array.isArray(obj))
    return obj.map((item) =>
      toSnakeCase(item as Record<string, unknown>),
    ) as unknown as Record<string, unknown>;

  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj)) {
    const snakeKey = key.replace(
      /[A-Z]/g,
      (letter) => `_${letter.toLowerCase()}`,
    );
    result[snakeKey] =
      typeof value === "object" && value !== null
        ? toSnakeCase(value as Record<string, unknown>)
        : value;
  }
  return result;
}

// ============================================================================
// TEACHERS
// ============================================================================

export async function getTeachers(params?: {
  isActive?: boolean;
}): Promise<AdminTeacher[]> {
  const response = await api.get("/admin/teachers/", {
    params: {
      is_active: params?.isActive,
    },
  });
  const data = Array.isArray(response.data) ? response.data : [];
  return data.map((item: Record<string, unknown>) =>
    toCamelCase(item),
  ) as unknown as AdminTeacher[];
}

export async function getTeacher(id: string): Promise<AdminTeacher> {
  const response = await api.get(`/admin/teachers/${id}/`);
  return toCamelCase(response.data) as unknown as AdminTeacher;
}

export async function createTeacher(
  data: CreateTeacherRequest,
): Promise<AdminTeacher> {
  const response = await api.post(
    "/admin/teachers/",
    toSnakeCase(data as unknown as Record<string, unknown>),
  );
  return toCamelCase(response.data) as unknown as AdminTeacher;
}

export async function updateTeacher(
  id: string,
  data: UpdateTeacherRequest,
): Promise<AdminTeacher> {
  const response = await api.patch(
    `/admin/teachers/${id}/`,
    toSnakeCase(data as unknown as Record<string, unknown>),
  );
  return toCamelCase(response.data) as unknown as AdminTeacher;
}

export async function deleteTeacher(id: string): Promise<void> {
  await api.delete(`/admin/teachers/${id}/`);
}

export async function reactivateTeacher(id: string): Promise<void> {
  await api.post(`/admin/teachers/${id}/reactivate/`);
}

export async function resetTeacherPassword(
  id: string,
): Promise<{ newPassword: string }> {
  const response = await api.post(`/admin/teachers/${id}/reset_password/`);
  return { newPassword: response.data.new_password };
}

export async function hardDeleteTeacher(id: string): Promise<void> {
  await api.delete(`/admin/teachers/${id}/hard_delete/`);
}

// ============================================================================
// STUDENTS
// ============================================================================

export async function getStudents(params?: {
  classGroup?: string;
  level?: string;
  isActive?: boolean;
}): Promise<AdminStudent[]> {
  const response = await api.get("/admin/students/", {
    params: {
      class_group: params?.classGroup,
      level: params?.level,
      is_active: params?.isActive,
    },
  });
  const data = Array.isArray(response.data) ? response.data : [];
  return data.map((item: Record<string, unknown>) =>
    toCamelCase(item),
  ) as unknown as AdminStudent[];
}

export async function getStudent(id: string): Promise<AdminStudent> {
  const response = await api.get(`/admin/students/${id}/`);
  return toCamelCase(response.data) as unknown as AdminStudent;
}

export async function createStudent(
  data: CreateStudentRequest,
): Promise<AdminStudent> {
  const response = await api.post(
    "/admin/students/",
    toSnakeCase(data as unknown as Record<string, unknown>),
  );
  return toCamelCase(response.data) as unknown as AdminStudent;
}

export async function updateStudent(
  id: string,
  data: UpdateStudentRequest,
): Promise<AdminStudent> {
  const response = await api.patch(
    `/admin/students/${id}/`,
    toSnakeCase(data as unknown as Record<string, unknown>),
  );
  return toCamelCase(response.data) as unknown as AdminStudent;
}

export async function deleteStudent(id: string): Promise<void> {
  await api.delete(`/admin/students/${id}/`);
}

export async function moveStudentToClass(
  id: string,
  data: MoveStudentRequest,
): Promise<void> {
  await api.post(
    `/admin/students/${id}/move_to_class/`,
    toSnakeCase(data as unknown as Record<string, unknown>),
  );
}

export async function reactivateStudent(id: string): Promise<void> {
  await api.post(`/admin/students/${id}/reactivate/`);
}

export async function hardDeleteStudent(id: string): Promise<void> {
  await api.delete(`/admin/students/${id}/hard_delete/`);
}

export async function resetStudentPassword(id: string): Promise<{ newPassword: string }> {
  const response = await api.post(`/admin/students/${id}/reset_password/`);
  return response.data;
}

// ============================================================================
// CLASS GROUPS
// ============================================================================

export async function getClassGroups(params?: {
  level?: string;
  teacher?: string;
  isActive?: boolean;
}): Promise<AdminClassGroup[]> {
  const response = await api.get("/admin/classes/", {
    params: {
      level: params?.level,
      teacher: params?.teacher,
      is_active: params?.isActive,
    },
  });
  const data = Array.isArray(response.data) ? response.data : [];
  return data.map((item: Record<string, unknown>) =>
    toCamelCase(item),
  ) as unknown as AdminClassGroup[];
}

export async function getClassGroup(id: string): Promise<AdminClassGroup> {
  const response = await api.get(`/admin/classes/${id}/`);
  return toCamelCase(response.data) as unknown as AdminClassGroup;
}

export async function createClassGroup(
  data: CreateClassGroupRequest,
): Promise<AdminClassGroup> {
  const response = await api.post(
    "/admin/classes/",
    toSnakeCase(data as unknown as Record<string, unknown>),
  );
  return toCamelCase(response.data) as unknown as AdminClassGroup;
}

export async function updateClassGroup(
  id: string,
  data: UpdateClassGroupRequest,
): Promise<AdminClassGroup> {
  const response = await api.patch(
    `/admin/classes/${id}/`,
    toSnakeCase(data as unknown as Record<string, unknown>),
  );
  return toCamelCase(response.data) as unknown as AdminClassGroup;
}

export async function deleteClassGroup(id: string): Promise<void> {
  await api.delete(`/admin/classes/${id}/`);
}

export async function reactivateClassGroup(id: string): Promise<void> {
  await api.post(`/admin/classes/${id}/reactivate/`);
}

export async function hardDeleteClassGroup(id: string): Promise<void> {
  await api.delete(`/admin/classes/${id}/hard_delete/`);
}

export async function bulkPriceClasses(
  data: BulkClassPriceRequest,
): Promise<{ updated: number }> {
  const response = await api.post(
    "/admin/classes/bulk_price/",
    toSnakeCase(data as unknown as Record<string, unknown>),
  );
  return response.data;
}

// ============================================================================
// LEVELS
// ============================================================================

export async function getLevels(): Promise<AdminLevel[]> {
  const response = await api.get("/admin/levels/");
  const data = Array.isArray(response.data) ? response.data : [];
  return data.map((item: Record<string, unknown>) =>
    toCamelCase(item),
  ) as unknown as AdminLevel[];
}

export async function createLevel(
  data: CreateLevelRequest,
): Promise<AdminLevel> {
  const response = await api.post(
    "/admin/levels/",
    toSnakeCase(data as unknown as Record<string, unknown>),
  );
  return toCamelCase(response.data) as unknown as AdminLevel;
}

export async function updateLevel(
  id: string,
  data: UpdateLevelRequest,
): Promise<AdminLevel> {
  const response = await api.patch(
    `/admin/levels/${id}/`,
    toSnakeCase(data as unknown as Record<string, unknown>),
  );
  return toCamelCase(response.data) as unknown as AdminLevel;
}

export async function deleteLevel(id: string): Promise<void> {
  await api.delete(`/admin/levels/${id}/`);
}

// ============================================================================
// LESSONS
// ============================================================================

export async function getLessons(params?: {
  level?: string;
}): Promise<AdminLesson[]> {
  const response = await api.get("/admin/lessons/", {
    params: { level: params?.level },
  });
  const data = Array.isArray(response.data) ? response.data : [];
  return data.map((item: Record<string, unknown>) =>
    toCamelCase(item),
  ) as unknown as AdminLesson[];
}

export async function createLesson(
  data: CreateLessonRequest,
): Promise<AdminLesson> {
  const response = await api.post(
    "/admin/lessons/",
    toSnakeCase(data as unknown as Record<string, unknown>),
  );
  return toCamelCase(response.data) as unknown as AdminLesson;
}

export async function updateLesson(
  id: string,
  data: UpdateLessonRequest,
): Promise<AdminLesson> {
  const response = await api.patch(
    `/admin/lessons/${id}/`,
    toSnakeCase(data as unknown as Record<string, unknown>),
  );
  return toCamelCase(response.data) as unknown as AdminLesson;
}

export async function deleteLesson(id: string): Promise<void> {
  await api.delete(`/admin/lessons/${id}/`);
}

export async function reorderLessons(
  level: string,
  items: Array<{ id: string; order: number }>,
): Promise<AdminLesson[]> {
  const response = await api.post("/admin/lessons/bulk_reorder/", {
    level,
    items,
  });
  const data = Array.isArray(response.data) ? response.data : [];
  return data.map((item: Record<string, unknown>) =>
    toCamelCase(item),
  ) as unknown as AdminLesson[];
}

// ============================================================================
// CLASS LESSON PLAN (per-class override of the level's lessons)
// ============================================================================

/** Resolved plan for a class group: custom entries if any, else level defaults. */
export async function getClassLessonPlan(
  classGroupId: string,
): Promise<ClassLessonPlanResponse> {
  const response = await api.get("/admin/class-lesson-plans/effective/", {
    params: { class_group: classGroupId },
  });
  return toCamelCase(response.data) as unknown as ClassLessonPlanResponse;
}

/** Materialize: snapshot the level's lessons into editable plan entries. */
export async function customizeClassLessonPlan(
  classGroupId: string,
): Promise<ClassLessonPlanResponse> {
  const response = await api.post("/admin/class-lesson-plans/customize/", {
    class_group: classGroupId,
  });
  return toCamelCase(response.data) as unknown as ClassLessonPlanResponse;
}

/** Reset back to the level default (delete the class's plan entries). */
export async function resetClassLessonPlan(
  classGroupId: string,
): Promise<ClassLessonPlanResponse> {
  const response = await api.post("/admin/class-lesson-plans/reset/", {
    class_group: classGroupId,
  });
  return toCamelCase(response.data) as unknown as ClassLessonPlanResponse;
}

export async function reorderClassLessonPlan(
  classGroupId: string,
  items: Array<{ id: string; order: number }>,
): Promise<ClassLessonPlanResponse> {
  const response = await api.post("/admin/class-lesson-plans/reorder/", {
    class_group: classGroupId,
    items,
  });
  return toCamelCase(response.data) as unknown as ClassLessonPlanResponse;
}

/** Replace the whole plan in one call (batch save from the editor). */
export async function setClassLessonPlan(
  classGroupId: string,
  items: Array<{ lesson: string; order: number; monthAvailable: number }>,
): Promise<ClassLessonPlanResponse> {
  const response = await api.post("/admin/class-lesson-plans/set_plan/", {
    class_group: classGroupId,
    items: items.map((it) => ({
      lesson: it.lesson,
      order: it.order,
      month_available: it.monthAvailable,
    })),
  });
  return toCamelCase(response.data) as unknown as ClassLessonPlanResponse;
}

export async function addClassLessonPlanItem(data: {
  classGroup: string;
  lesson: string;
  order: number;
  monthAvailable: number;
}): Promise<AdminClassLessonPlanItem> {
  const response = await api.post(
    "/admin/class-lesson-plans/",
    toSnakeCase(data as unknown as Record<string, unknown>),
  );
  return toCamelCase(response.data) as unknown as AdminClassLessonPlanItem;
}

export async function updateClassLessonPlanItem(
  id: string,
  data: { lesson?: string; monthAvailable?: number; order?: number },
): Promise<AdminClassLessonPlanItem> {
  const response = await api.patch(
    `/admin/class-lesson-plans/${id}/`,
    toSnakeCase(data as unknown as Record<string, unknown>),
  );
  return toCamelCase(response.data) as unknown as AdminClassLessonPlanItem;
}

export async function removeClassLessonPlanItem(id: string): Promise<void> {
  await api.delete(`/admin/class-lesson-plans/${id}/`);
}

// ============================================================================
// CONTRACTS
// ============================================================================

export async function getContracts(): Promise<AdminContract[]> {
  const response = await api.get("/admin/contracts/");
  const data = Array.isArray(response.data) ? response.data : [];
  return data.map((item: Record<string, unknown>) =>
    toCamelCase(item),
  ) as unknown as AdminContract[];
}

export async function updateContract(
  id: string,
  data: UpdateContractRequest,
): Promise<AdminContract> {
  const response = await api.patch(
    `/admin/contracts/${id}/`,
    toSnakeCase(data as unknown as Record<string, unknown>),
  );
  return toCamelCase(response.data) as unknown as AdminContract;
}

/** Create a class-specific contract override (bumps nothing; starts at v1). */
export async function createContractOverride(
  data: CreateContractOverrideRequest,
): Promise<AdminContract> {
  const response = await api.post(
    "/admin/contracts/",
    toSnakeCase(data as unknown as Record<string, unknown>),
  );
  return toCamelCase(response.data) as unknown as AdminContract;
}

/** Delete a class-specific override → the class falls back to the type default. */
export async function deleteContractOverride(id: string): Promise<void> {
  await api.delete(`/admin/contracts/${id}/`);
}
